export const activationFeatures = ['registration', 'google', 'community', 'portfolio', 'equipment', 'exif', 'mail'] as const;
export type ActivationFeature = typeof activationFeatures[number];
export type ActivationItem = {requested: boolean; prerequisitesPassed: boolean; enabled: boolean; blockers: string[]};
export type ActivationStatus = {
  features: Record<ActivationFeature, ActivationItem>;
  schemas: Record<string, boolean>;
  verificationScope: 'local_configuration_and_database_metadata';
  workers: {mailCommandAvailable: boolean; schedulerStatus: 'not_verified'; financialAutomationManagedHere: false};
  paymentSettingsChanged: false;
};

export const activationCopy: Record<ActivationFeature, {title: string; description: string}> = {
  registration: {title: 'Nuovi account', description: 'Registrazione con nome, cognome ed e-mail da confermare.'},
  google: {title: 'Accesso con Google', description: 'Identità, e-mail e profilo. Nessun accesso alla posta Gmail.'},
  community: {title: 'Fotografie della community', description: 'Pubblicazione degli scatti con fotocamera e obiettivo, filtri e segnalazioni.'},
  portfolio: {title: 'Portfolio e fotografi', description: 'Profili fotografici, scatti selezionati, località, seguiti e contatto nella chat di Gruvo.'},
  equipment: {title: 'Catalogo condiviso', description: 'Ricerca dei modelli e proposte di nuove fotocamere e obiettivi da verificare.'},
  exif: {title: 'Informazioni delle fotografie', description: 'Lettura dei metadati consentiti. Non certifica autenticità né numero di scatti.'},
  mail: {title: 'Comunicazioni degli ordini', description: 'Coda delle e-mail per acquisti e spedizioni. Non iscrive nessuno a una newsletter.'},
};

const blockers: Record<string, string> = {
  database: 'Collegamento agli archivi del sito da completare.',
  legalDocuments: 'Condizioni e informativa privacy definitive da confermare.',
  publishedTerms: 'Versione definitiva delle condizioni da pubblicare; la bozza non basta.',
  accountSchema: 'Archivi degli account e dei dati personali da aggiornare.',
  testedMail: 'Invio e-mail e indirizzo di assistenza da confermare con una prova reale.',
  identitySchema: 'Archivio dei collegamenti Google da preparare.',
  googleConfiguration: 'Collegamento Google o requisiti di sicurezza del server da completare.',
  registrationRequested: 'Attiva prima la registrazione degli account.',
  communityRequested: 'Attiva prima la pubblicazione delle fotografie.',
  communitySchema: 'Archivi delle fotografie, dei filtri e delle segnalazioni da aggiornare.',
  portfolioSchema: 'Archivi dei portfolio, dei seguiti e dei contatti da aggiornare.',
  safeImageUploads: 'Caricamento ed elaborazione sicura delle immagini da verificare.',
  equipmentSchema: 'Archivio dei modelli proposti e verificati da preparare.',
  exifProcessor: 'Lettura dei metadati delle foto non disponibile su questo server.',
  mailSchema: 'Archivio delle comunicazioni automatiche da preparare.',
};

export function activationBlockerText(code: string): string {
  return blockers[code] ?? 'È necessario completare un controllo tecnico prima di aprire questa funzione.';
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Reject partial or contradictory server responses instead of painting an unconfirmed success. */
export function normalizeActivationStatus(value: unknown): ActivationStatus {
  const invalid = () => new Error('Stato delle funzioni non confermato. Aggiorna la verifica.');
  if (!record(value) || !record(value.features) || !record(value.schemas) || !record(value.workers)
    || value.verificationScope !== 'local_configuration_and_database_metadata' || value.paymentSettingsChanged !== false
    || value.workers.schedulerStatus !== 'not_verified' || value.workers.financialAutomationManagedHere !== false
    || typeof value.workers.mailCommandAvailable !== 'boolean') throw invalid();
  const features = {} as ActivationStatus['features'];
  for (const feature of activationFeatures) {
    const item = value.features[feature];
    if (!record(item) || typeof item.requested !== 'boolean' || typeof item.prerequisitesPassed !== 'boolean' || typeof item.enabled !== 'boolean'
      || !Array.isArray(item.blockers) || item.blockers.length > 20 || item.blockers.some(code => typeof code !== 'string' || !/^[a-zA-Z]{1,60}$/.test(code))
      || item.prerequisitesPassed !== (item.blockers.length === 0) || item.enabled !== (item.requested && item.prerequisitesPassed)) throw invalid();
    features[feature] = {requested: item.requested, prerequisitesPassed: item.prerequisitesPassed, enabled: item.enabled, blockers: [...item.blockers] as string[]};
  }
  const schemas: Record<string, boolean> = {};
  for (const [name, ready] of Object.entries(value.schemas)) {
    if (!/^[a-z]{1,30}$/.test(name) || typeof ready !== 'boolean') throw invalid();
    schemas[name] = ready;
  }
  return {features, schemas, verificationScope: value.verificationScope, paymentSettingsChanged: false,
    workers: {mailCommandAvailable: value.workers.mailCommandAvailable, schedulerStatus: 'not_verified', financialAutomationManagedHere: false}};
}

export function readyActivationBatch(status: ActivationStatus): Partial<Record<ActivationFeature, boolean>> {
  return Object.fromEntries(activationFeatures.filter(feature => status.features[feature].prerequisitesPassed && !status.features[feature].requested).map(feature => [feature, true]));
}
